import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Box,
  Fade,
  InputLabel,
} from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import PropTypes from 'prop-types';

const FilterSelect = ({
  type = 'select',
  value,
  onChange,
  options = [],
  placeholder,
  label,
  allOption = false,
  allOptionLabel = 'All',
  allOptionValue = 'all',
  sx = {},
  PopperProps = {},
  ...props
}) => {
  const baseSelectStyles = {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    color: '#111827',
    border: '1px solid #E5E7EB',
    background: '#FFFFFF',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-select': {
      py: 0.75,
      px: 1.25,
    },
    '& .MuiSelect-icon': {
      color: '#6B7280',
      transition: 'transform .2s ease',
      fontSize: '1.25rem',
    },
    '&:hover': {
      borderColor: '#D1D5DB',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: '#1B2640',
      boxShadow: '0 0 0 3px rgba(27, 38, 64, 0.1)',
    },
    '&.Mui-expanded .MuiSelect-icon': {
      transform: 'rotate(180deg)',
    },
  };

  const inputLabelStyles = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.875rem',
    color: '#6B7280',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#1B2640',
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  };

  const menuProps = {
    TransitionComponent: Fade,
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
      },
    },
    MenuListProps: { dense: true, sx: { py: 0.5 } },
  };

  const menuItemStyles = {
    fontSize: '0.875rem',
    px: 1.5,
    py: 0.875,
    fontFamily: 'Inter, sans-serif',
    transition: 'background-color .15s ease',
    '&:hover': { 
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: '#EEF2FF',
      color: '#1B2640',
      fontWeight: 600,
    },
  };

  const autocompleteStyles = {
    width: { xs: '100%', sm: 'auto' },
    minWidth: 'fit-content',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    color: '#111827',
    border: '1px solid #E5E7EB',
    background: '#FFFFFF',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#D1D5DB',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: '#1B2640',
      boxShadow: '0 0 0 3px rgba(27, 38, 64, 0.1)',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      bgcolor: 'transparent',
      border: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiInputBase-input': {
        py: 0.75,
        px: 1.25,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: '1.5',
        color: value ? '#111827' : '#6B7280',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root': {
          color: '#6B7280',
          transition: 'transform .2s ease',
          fontSize: '1.25rem',
        },
      },
      '&.Mui-expanded .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
  };

  if (type === 'autocomplete') {
    const autocompleteInputLabelStyles = {
      fontFamily: 'Inter, sans-serif',
      fontSize: '0.875rem',
      color: '#6B7280',
      fontWeight: 500,
      '&.Mui-focused': {
        color: '#1B2640',
      },
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    };

    return (
      <FormControl 
        size="small" 
        sx={{ 
          width: { xs: '100%', sm: 'auto' }, 
          minWidth: label ? 160 : 'fit-content',
          ...sx 
        }}
      >
        {label && (
          <InputLabel 
            id={`filter-autocomplete-label-${label}`}
            sx={autocompleteInputLabelStyles}
            shrink={!!value}
          >
            {label}
          </InputLabel>
        )}
        <Autocomplete
          size="small"
          options={options}
          value={value}
          onChange={onChange}
          popupIcon={<KeyboardArrowDownRounded />}
          disablePortal={false}
          sx={{ ...autocompleteStyles, ...sx }}
          renderInput={(params) => (
            <TextField
              {...params}
              labelId={label ? `filter-autocomplete-label-${label}` : undefined}
              placeholder={!label ? placeholder : ''}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  height: 'auto',
                  border: 'none',
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#6B7280',
                  opacity: 1,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                },
              }}
            />
          )}
        PaperComponent={({ children, ...other }) => (
          <Box
            component="div"
            {...other}
            sx={{
              mt: 0.5,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              fontFamily: 'Inter, sans-serif',
              backgroundColor: '#FFFFFF',
              zIndex: 10001,
              position: 'relative',
              display: 'block',
            }}
          >
            {children}
          </Box>
        )}
        ListboxProps={{
          sx: {
            py: 0.5,
            maxHeight: '300px',
            overflow: 'auto',
            '& .MuiAutocomplete-option': {
              fontSize: '0.875rem',
              px: 1.5,
              py: 0.875,
              fontFamily: 'Inter, sans-serif',
              transition: 'background-color .15s ease',
              '&:hover': { 
                backgroundColor: '#F3F4F6',
              },
              '&.Mui-focused': { 
                backgroundColor: '#F3F4F6',
              },
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: '#EEF2FF',
                color: '#1B2640',
                fontWeight: 600,
              },
            },
          },
        }}
        componentsProps={{
          popper: {
            style: { zIndex: 10001 },
            placement: 'bottom-start',
            disablePortal: false,
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 4],
                },
              },
            ],
          },
        }}
        PopperProps={{
          style: { zIndex: 10001 },
          placement: 'bottom-start',
          disablePortal: false,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ],
          ...PopperProps,
        }}
        {...props}
      />
      </FormControl>
    );
  }

  const displayValue = value === allOptionValue || !value ? '' : value;
  const selectedOption = options.find(
    (opt) => (typeof opt === 'string' ? opt : opt.value) === value
  );
  const displayText = selectedOption
    ? typeof selectedOption === 'string'
      ? selectedOption
      : selectedOption.label
    : '';

  const selectStyles = {
    ...baseSelectStyles,
    '& .MuiSelect-select': {
      ...baseSelectStyles['& .MuiSelect-select'],
      color: displayValue ? '#111827' : '#6B7280',
    },
  };

  return (
    <FormControl 
      size="small" 
      sx={{ 
        width: { xs: '100%', sm: 'auto' }, 
        minWidth: label ? 160 : 'fit-content',
        ...sx 
      }}
    >
      {label && (
        <InputLabel 
          id={`filter-select-label-${label}`}
          sx={inputLabelStyles}
          shrink={!!displayValue}
        >
          {label}
        </InputLabel>
      )}
      <Select
        labelId={label ? `filter-select-label-${label}` : undefined}
        label={label}
        value={displayValue}
        onChange={onChange}
        displayEmpty={!label}
        renderValue={(selected) => {
          if (!selected || selected === allOptionValue) {
            return label ? '' : (allOption ? allOptionLabel : '');
          }
          return displayText || selected;
        }}
        IconComponent={KeyboardArrowDownRounded}
        MenuProps={menuProps}
        sx={selectStyles}
        {...props}
      >
        {allOption && (
          <MenuItem value={allOptionValue} sx={menuItemStyles}>
            {allOptionLabel}
          </MenuItem>
        )}
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          return (
            <MenuItem key={optionValue} value={optionValue} sx={menuItemStyles}>
              {optionLabel}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

FilterSelect.propTypes = {
  type: PropTypes.oneOf(['select', 'autocomplete']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ),
  placeholder: PropTypes.string,
  label: PropTypes.string,
  allOption: PropTypes.bool,
  allOptionLabel: PropTypes.string,
  allOptionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
  PopperProps: PropTypes.object,
};

export default FilterSelect;

